import React from 'react';

import { Box, Flex } from '@atlaskit/primitives/compiled';

import type { ContentMode } from '@confluence/content-types-utils/entry-points/getContentMode';
import { ContentTypeClassification } from '@confluence/data-classification/entry-points/ContentTypeClassification';
import { ClassificationEditModal } from '@confluence/data-classification/entry-points/ClassificationEditModal';
import {
	useContentClassification,
	type ClassificationQueryContext,
} from '@confluence/data-classification/entry-points/queries';
import {
	useIsClassificationEnabledForContentTypeExpansion,
	useGetClassificationTagUIVariant,
} from '@confluence/data-classification/entry-points/hooks';
import { fg } from '@confluence/feature-gating';
import { LoadableAfterPaint } from '@confluence/loadable';
import type { ShareContentType } from '@confluence/share/entry-points/ShareButton';

import type { FragmentRef } from '@confluence/apollo-relay-interop/entry-points/useFragmentizedQuery';

import type { ContentTypesHeaderQueryNode } from '../ContentTypesHeader';
import { useContentBasicInfo } from '../useContentBasicInfo';
import { useContentSpace } from '../useContentSpace';

export type ContentTypesHeaderClassificationProps = {
	contentId: string;
	contentData?: ContentTypesHeaderQueryNode | null;
	contentBasicInfoRef?: FragmentRef;
	contentSpaceRef?: FragmentRef;
	contentStatusContext: ClassificationQueryContext;
	contentType: {
		type: ShareContentType;
		mode?: ContentMode;
	};
	readOnly: boolean;
	isSpaceOverviewPage: boolean;
	isObjectHeader: boolean;
};

export const ContentTypesHeaderClassification = ({
	contentId,
	contentData,
	contentBasicInfoRef,
	contentSpaceRef,
	contentStatusContext,
	contentType,
	readOnly,
	isSpaceOverviewPage,
	isObjectHeader,
}: ContentTypesHeaderClassificationProps) => {
	let spaceKey: string, contentBasicInfo;
	if (contentBasicInfoRef && contentSpaceRef) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		contentBasicInfo = useContentBasicInfo(contentBasicInfoRef);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		spaceKey = useContentSpace(contentSpaceRef)?.key ?? '';
	} else {
		contentBasicInfo = contentData;
		spaceKey = contentData?.space?.key ?? '';
	}

	//Skip classification enabled query if flag is enabled
	const classificationQueryRequired = useIsClassificationEnabledForContentTypeExpansion(
		contentBasicInfo?.type ?? '',
		false,
		fg('disable_classification_enabled_query'),
	);

	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext,
		shouldSkipQueries: fg('disable_classification_enabled_query')
			? false
			: !classificationQueryRequired,
		shouldReadFromQueries: true,
	});

	//Get classification enablement from classification metadata if flag is enabled
	const isClassificationEnabled = fg('disable_classification_enabled_query')
		? (contentClassificationMetadata?.featureEnabled ?? false)
		: classificationQueryRequired;

	const classificationTagUiVariant = useGetClassificationTagUIVariant({
		contentTitle: contentBasicInfo?.title ?? '',
		classificationName: contentClassificationMetadata.classification?.name,
	});

	if (
		(contentType.type === 'whiteboard' ||
			contentType.type === 'database' ||
			contentType.type === 'embed' ||
			!isObjectHeader) && // from renderOriginalHeader in ContentTypesHeader
		isClassificationEnabled
	) {
		return (
			<Flex testId="content-types-header-classification">
				<ContentTypeClassification
					variant={classificationTagUiVariant}
					spaceKey={spaceKey}
					contentId={contentId}
					contentType={contentType.type}
					contentClassificationMetadata={contentClassificationMetadata}
					readOnly={readOnly}
				/>
				<ClassificationEditModal
					contentId={contentId}
					spaceKey={spaceKey}
					contentClassificationMetadata={contentClassificationMetadata}
				/>
			</Flex>
		);
	} else if (
		contentType.mode === 'live' ||
		contentType.mode === 'draft' ||
		contentType.mode === 'edit'
	) {
		return null;
	} else if (isSpaceOverviewPage) {
		// we want to render the classification tag in the header for space overview pages
		return (
			<Box testId="content-classification-tag">
				<ContentClassificationTagLoader
					contentId={contentId}
					contentType={contentType.type || null}
					contentClassificationMetadata={contentClassificationMetadata}
					isEditMode={false}
				/>
			</Box>
		);
	}

	return null;
};

// duplicated from ContentHeader
const ContentClassificationTagLoader = LoadableAfterPaint({
	name: 'ContentClassificationTag',
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-confluencedata-classificationentry-pointsContentClassificationTag" */ '@confluence/data-classification/entry-points/ContentClassificationTag'
			)
		).ContentClassificationTag,
});
